/**
 * `useProjectTasks` — live read of every task scoped to a single
 * project. Mirrors the mobile `src/features/tasks/useTasks.ts` hook.
 *
 * Query: `tasks where orgId == orgId AND projectId == projectId`.
 * Rules gate read access to org members.
 *
 * Sort: `createdAt` desc.
 */
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';

export type TaskStatus = 'not_started' | 'ongoing' | 'completed';
export type TaskPriority = 'low' | 'medium' | 'high';

export type TaskItem = {
  id: string;
  orgId: string;
  projectId: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  category?: string;
  startDate?: { toMillis(): number } | null;
  endDate?: { toMillis(): number } | null;
  progress: number;
  assignedTo?: string;
  assignedToName?: string;
  photoUris?: string[];
  createdBy?: string;
  createdAt?: { toMillis(): number } | null;
  updatedAt?: { toMillis(): number } | null;
};

export type UseProjectTasksResult = {
  tasks: TaskItem[];
  loading: boolean;
  error: string | null;
};

export function useProjectTasks(
  orgId: string | null | undefined,
  projectId: string | undefined,
): UseProjectTasksResult {
  const [items, setItems] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId || !projectId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      query(
        collection(db, 'tasks'),
        where('orgId', '==', orgId),
        where('projectId', '==', projectId),
      ),
      (snap) => {
        setItems(snap.docs.map((d) => ({ ...(d.data() as TaskItem), id: d.id })));
        setLoading(false);
      },
      (err) => {
        console.warn('[useProjectTasks] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [orgId, projectId]);

  const sorted = useMemo(
    () =>
      [...items].sort((a, b) => {
        const am = a.createdAt?.toMillis?.() ?? 0;
        const bm = b.createdAt?.toMillis?.() ?? 0;
        return bm - am;
      }),
    [items],
  );

  return { tasks: sorted, loading, error };
}

// ───── Display helpers ─────

export function taskStatusLabel(s: TaskStatus): string {
  switch (s) {
    case 'not_started': return 'Not started';
    case 'ongoing':     return 'Ongoing';
    case 'completed':   return 'Completed';
  }
}

export function taskPriorityLabel(p: TaskPriority): string {
  return p[0].toUpperCase() + p.slice(1);
}
